/**
 * Export Comparison Button Component
 *
 * A button that generates and exports a fund comparison summary
 * as a PDF, shareable image, or link.
 *
 * Features:
 * - PDF generation with comparison table, charts, and insights
 * - Image export with Web Share API fallback
 * - Shareable link generation
 * - Toast notifications for progress and status
 */

import * as React from "react";
import { FileText, Loader2, Image, Link2, Share2 } from "lucide-react";
import { Button, type buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";
import type { Fund } from "@curie/shared";
import {
  useExportComparison,
  type ExportFormat,
  type ComparisonExportOptions,
} from "@/hooks/useExportComparison";

// ============================================================================
// Types
// ============================================================================

export type { ExportFormat, ComparisonExportOptions };

export interface ExportComparisonButtonProps
  extends Omit<React.ComponentProps<"button">, "onClick">,
    VariantProps<typeof buttonVariants> {
  /** The element ref to capture for export */
  targetRef: React.RefObject<HTMLElement | null>;
  /** Funds being compared */
  funds: Fund[];
  /** Optional custom filename */
  fileName?: string;
  /** Optional label to show alongside the icon */
  showLabel?: boolean;
  /** Custom label text */
  label?: string;
  /** Callback fired after successful export */
  onExportSuccess?: (format: ExportFormat | "link") => void;
  /** Callback fired after export failure */
  onExportError?: (error: Error) => void;
  /** Whether to show all export options or just primary ones */
  showAllOptions?: boolean;
  /** Custom title for the exported document */
  exportTitle?: string;
  /** Custom subtitle for the exported document */
  exportSubtitle?: string;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_LABEL = "Export";

// ============================================================================
// Main Component
// ============================================================================

/**
 * ExportComparisonButton
 *
 * A button component that exports a fund comparison as PDF, image, or shareable link.
 *
 * @example
 * ```tsx
 * function CompareView() {
 *   const contentRef = useRef<HTMLDivElement>(null);
 *
 *   return (
 *     <div>
 *       <ExportComparisonButton
 *         targetRef={contentRef}
 *         funds={fundsToCompare}
 *         showLabel
 *       />
 *       <div ref={contentRef}>
 *         {/* Comparison content *\/}
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function ExportComparisonButton({
  targetRef,
  funds,
  fileName,
  showLabel = false,
  label = DEFAULT_LABEL,
  variant = "outline",
  size = "sm",
  className,
  onExportSuccess,
  onExportError,
  showAllOptions = true,
  exportTitle,
  exportSubtitle,
  disabled,
  ...props
}: ExportComparisonButtonProps) {
  const {
    exportTo,
    isExporting,
    exportingFormat,
    canShareImages,
    copyShareLink,
  } = useExportComparison(targetRef, funds);

  const handleExport = React.useCallback(
    async (format: ExportFormat) => {
      try {
        await exportTo(format, {
          fileName,
          title: exportTitle,
          subtitle: exportSubtitle,
        });
        onExportSuccess?.(format);
      } catch (error) {
        const err = error instanceof Error ? error : new Error("Export failed");
        onExportError?.(err);
      }
    },
    [exportTo, fileName, exportTitle, exportSubtitle, onExportSuccess, onExportError]
  );

  const handleCopyLink = React.useCallback(async () => {
    try {
      await copyShareLink();
      onExportSuccess?.("link");
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Failed to copy link");
      onExportError?.(err);
    }
  }, [copyShareLink, onExportSuccess, onExportError]);

  // Track whether the ref has a target (check on mount and on fund changes)
  const [hasTarget, setHasTarget] = React.useState(false);
  React.useEffect(() => {
    setHasTarget(!!targetRef.current);
  }, [targetRef, funds]);

  // Disabled state if no funds or no target
  const isDisabled = disabled || isExporting || !hasTarget || funds.length < 2;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          className={cn("gap-2", className)}
          disabled={isDisabled}
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
          title={funds.length < 2 ? "Add at least 2 funds to export" : label}
          {...props}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Share2 className="h-4 w-4" />
          )}
          {showLabel && <span>{isExporting ? "Exporting..." : label}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Export Comparison</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* PDF Option */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleExport("pdf");
          }}
          disabled={isExporting}
          className="cursor-pointer gap-2"
        >
          {exportingFormat === "pdf" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          <div className="flex flex-col">
            <span>Download PDF</span>
            <span className="text-xs text-muted-foreground">
              Full comparison report
            </span>
          </div>
        </DropdownMenuItem>

        {/* Image Option */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleExport("image");
          }}
          disabled={isExporting}
          className="cursor-pointer gap-2"
        >
          {exportingFormat === "image" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Image className="h-4 w-4" />
          )}
          <div className="flex flex-col">
            <span>{canShareImages ? "Share Image" : "Download Image"}</span>
            <span className="text-xs text-muted-foreground">
              {canShareImages ? "Share via apps" : "PNG screenshot"}
            </span>
          </div>
        </DropdownMenuItem>

        {showAllOptions && (
          <>
            <DropdownMenuSeparator />

            {/* Copy Link Option */}
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleCopyLink();
              }}
              disabled={isExporting}
              className="cursor-pointer gap-2"
            >
              <Link2 className="h-4 w-4" />
              <div className="flex flex-col">
                <span>Copy Link</span>
                <span className="text-xs text-muted-foreground">
                  Shareable comparison URL
                </span>
              </div>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Compact export button (icon only)
 */
export function ExportComparisonButtonCompact(
  props: Omit<ExportComparisonButtonProps, "showLabel">
) {
  return <ExportComparisonButton {...props} showLabel={false} />;
}

/**
 * Export button with full label
 */
export function ExportComparisonButtonWithLabel(
  props: Omit<ExportComparisonButtonProps, "showLabel">
) {
  return <ExportComparisonButton {...props} showLabel />;
}

export default ExportComparisonButton;
